import { useUser } from "@/contexts/UserContext";
import { useScopeParams } from "./useScopeParams";
import { toBackendTrialId } from "@/utils/trialIdMap";

export function useFeatureFlag(
  componentFlag: string | string[],
  anyTrialFeature = false,
): boolean {
  const { id: rawId, scope } = useScopeParams();
  const id = toBackendTrialId(rawId);
  const { userInfo } = useUser();

  if (!userInfo) return false;

  const componentFlags = Array.isArray(componentFlag)
    ? componentFlag
    : [componentFlag];

  // In portfolio scope there is no single trial ID — check across all trials.
  if (scope === "portfolio" || anyTrialFeature) {
    return (
      userInfo?.available_trials?.some((trial: any) =>
        componentFlags.some((flag) => trial.feature_flags?.includes(flag)),
      ) ?? false
    );
  }

  const thisTrialInfo = userInfo?.available_trials?.find(
    (trial: any) => trial.id === id,
  ) as any;

  return componentFlags.some(
    (flag) => thisTrialInfo?.feature_flags?.includes(flag) || false,
  );
}

/**
 * Feature flag for one named trial, regardless of the current scope.
 *
 * `useFeatureFlag` deliberately widens to "any granted trial" in portfolio
 * scope, which is right for flags that gate a portfolio screen. It is wrong for
 * flags that describe an individual trial — a portfolio can hold trials with
 * different grants, e.g. portfolio-only trials that carry no operational data
 * and so are not enterable. Use this hook for those.
 */
export function useTrialFeatureFlag(
  trialId: string | undefined,
  componentFlag: string | string[],
): boolean {
  const { userInfo } = useUser();

  if (!userInfo || !trialId) return false;

  const componentFlags = Array.isArray(componentFlag)
    ? componentFlag
    : [componentFlag];
  const id = toBackendTrialId(trialId);
  const trial = userInfo?.available_trials?.find(
    (candidate: any) => candidate.id === id,
  ) as any;

  return componentFlags.some(
    (flag) => trial?.feature_flags?.includes(flag) || false,
  );
}
